import type {
  ScenarioDriverType,
  ScenarioSummary,
  ScenarioDriver,
} from "@workspace/api-client-react";

export const DRIVER_TYPE_LABELS: Record<ScenarioDriverType, string> = {
  spend_reduction: "Spend Reduction",
  spend_increase: "Spend Increase",
  timing_shift: "Timing Shift",
  fiscal_year_shift: "Fiscal Year Shift",
  usage_volume: "Usage / Volume",
  price_rate: "Price / Rate",
  headcount: "Headcount",
  project: "Project",
  commitment: "Commitment",
  vendor_consolidation: "Vendor Consolidation",
  new_investment: "New Investment",
  licensing_model_change: "Licensing Model Change",
};

const PERIOD_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function periodLabel(p: number): string {
  return PERIOD_NAMES[p - 1] ?? `P${p}`;
}

export function formatTs(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

// ─── Management-question metadata ───────────────────────────────────────────────
// Friendly labels + short human plans for each API template key. Question keys
// come from the real API (scenarioQuestionTemplates); this maps them to plain
// language a presenter can read aloud.

export interface QuestionPlaybook {
  /** Short friendly label, used to build deterministic scenario titles. */
  shortLabel: string;
  /** One-line plain summary of the question. */
  summary: string;
  /** What the presenter enters to model the assumption. */
  input: string;
  /** What the driver actually changes in the model. */
  driverChange: string;
  /** What to look at once drivers are added (FY + Rolling 12M). */
  review: string;
  /** The concrete next action to take. */
  next: string;
}

export const QUESTION_PLAYBOOKS: Record<string, QuestionPlaybook> = {
  get_back_to_budget: {
    shortLabel: "Path back to budget",
    summary:
      "Explore management-selected levers to close a gap to budget without automatically choosing cuts.",
    input:
      "Add one or more reduction, timing, or vendor-consolidation drivers naming the specific account, period range, and amount or percentage you want to test.",
    driverChange:
      "Each driver lowers or reschedules spend against the authoritative baseline while preserving the underlying monthly phasing.",
    review:
      "Read FY Impact to see the full-year swing and Rolling 12-month Impact for the forward horizon. A favorable (F) impact means you have recovered budget.",
    next:
      "When the gap is closed and the scenario reconciles, promote it to a working Forecast for Review.",
  },
  fund_new_investment: {
    shortLabel: "Funding a new investment",
    summary:
      "Size a net-new investment and the offsetting funding levers that pay for it.",
    input:
      "Add a New Investment driver with the total amount, target period range, and an approval reference. Then add reduction or timing drivers as the funding source.",
    driverChange:
      "The investment adds spend that is not in the baseline; funding drivers reduce or defer other spend to offset it.",
    review:
      "Check FY Impact and Rolling 12-month Impact to confirm the net position — the goal is usually a near-neutral or fundable delta once offsets are applied.",
    next:
      "Once the investment is fully funded and reconciled, promote the scenario for Review.",
  },
  test_spend_change: {
    shortLabel: "Spend change test",
    summary:
      "Test an increase or reduction in a cost line while keeping monthly phasing intact.",
    input:
      "Add a spend-reduction, spend-increase, price/rate, or usage/volume driver with the account, period range, and either a dollar amount or a percentage.",
    driverChange:
      "The driver flexes the chosen account up or down across the period range, applying the change to the existing baseline shape.",
    review:
      "FY Impact shows the annual sensitivity; Rolling 12-month Impact shows the forward-looking swing. Compare both to judge how sensitive the plan is.",
    next:
      "Adjust the amount or percentage until the sensitivity matches the question being asked, then promote if it becomes the plan of record.",
  },
  test_operating_plan: {
    shortLabel: "Operating plan change",
    summary:
      "Test source-backed changes to people, projects, commitments, and vendors.",
    input:
      "Add headcount, project, commitment, vendor-consolidation, or fiscal-year-shift drivers. Provide costing inputs (salary, benefit rate) and source references where required.",
    driverChange:
      "Each driver layers a costed plan change onto the baseline; the engine pro-rates active months and honors source-backed commitments.",
    review:
      "FY Impact captures the in-year cost of the plan change; Rolling 12-month Impact shows the run-rate effect into next year.",
    next:
      "Confirm every source-backed driver has valid evidence, reconcile, then promote for Review.",
  },
  test_license_economics: {
    shortLabel: "Licensing model change",
    summary:
      "Compare immutable seat, usage, and hybrid License Economics assumptions — including reclaiming unused licenses.",
    input:
      "Add a Licensing Model Change driver, select a License Economics record, set the scenario model and effective timing, and optionally reclaim unused seats.",
    driverChange:
      "The driver re-prices the selected license under the scenario model, seat/usage overrides, and any explicit unused-seat reclaim, subject to the monthly minimum commitment.",
    review:
      "Use the Licensing tab for current-vs-scenario economics and breakeven, and the KPIs for FY Impact and Rolling 12-month Impact. Reclaim savings appear as a favorable impact.",
    next:
      "Validate the breakeven and timing, reconcile, then promote the licensing plan for Review.",
  },
};

export function questionShortLabel(
  questionKey: string,
  fallbackQuestion?: string
): string {
  const playbook = QUESTION_PLAYBOOKS[questionKey];
  if (playbook) return playbook.shortLabel;
  if (fallbackQuestion) return fallbackQuestion.replace(/\?$/, "");
  return "Scenario";
}

// ─── Machine-name detection + friendly titles ───────────────────────────────────
// Raw records may carry machine-like names (e.g. "SCN-DBmZMS") or driver names
// with random suffixes. We keep these only as secondary audit detail and derive
// a friendly, deterministic title from the management question and driver context.

// A "machine token": a short prefix + separator + a mixed/random tail, e.g.
// "scn-DBmZMS", "ms-1788408480260", "SCN_ab12cd". Case-insensitive.
const MACHINE_TOKEN = /^[A-Za-z]{1,6}[-_][A-Za-z0-9]{4,}$/;
// A long random-ish alnum/hex blob with no vowels-as-words structure.
const RANDOM_BLOB = /^[0-9a-fA-F-]{16,}$/;

function looksLikeCodeToken(token: string): boolean {
  if (MACHINE_TOKEN.test(token)) {
    // Reject if the tail reads like a real word (all letters, has a vowel and
    // is title/lowercase) — those are usually intentional, e.g. "co-owner".
    const tail = token.slice(token.search(/[-_]/) + 1);
    const hasDigit = /\d/.test(tail);
    const mixedCase = /[a-z]/.test(tail) && /[A-Z]/.test(tail);
    return hasDigit || mixedCase || tail.length >= 6;
  }
  return RANDOM_BLOB.test(token);
}

/** Heuristic: does this look like a generated code rather than a human phrase? */
export function isMachineName(name: string | null | undefined): boolean {
  if (!name) return true;
  const trimmed = name.trim();
  if (!trimmed) return true;
  // No spaces: treat the whole thing as one token to classify.
  if (!trimmed.includes(" ")) return looksLikeCodeToken(trimmed);
  // Has spaces (human phrase) but may end with an appended code suffix, e.g.
  // "What if our licensing model changes? ms-1788408480260". Those are still
  // human-readable once the suffix is stripped, so not "machine".
  return false;
}

/** Strip a trailing machine-code suffix from an otherwise human phrase. */
function stripCodeSuffix(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length > 1 && looksLikeCodeToken(parts[parts.length - 1])) {
    return parts.slice(0, -1).join(" ").replace(/[?:—-]\s*$/, "").trim();
  }
  return name.trim();
}

/**
 * Deterministic, friendly scenario title. Prefers a human name when the record
 * already has one; otherwise derives a title from the management question and
 * the driver context (department, first driver type).
 */
export function friendlyScenarioTitle(
  scenario: Pick<
    ScenarioSummary,
    "name" | "questionKey" | "departmentCode" | "fiscalYear"
  > & { drivers?: Array<Pick<ScenarioDriver, "type">> },
  fallbackQuestion?: string
): string {
  if (!isMachineName(scenario.name)) {
    // Human phrase — drop any appended code suffix for a clean title.
    const cleaned = stripCodeSuffix(scenario.name);
    return cleaned || questionShortLabel(scenario.questionKey, fallbackQuestion);
  }

  const base = questionShortLabel(scenario.questionKey, fallbackQuestion);
  const driverType = scenario.drivers?.[0]?.type;
  const driverLabel = driverType ? DRIVER_TYPE_LABELS[driverType] : "";
  const dept = scenario.departmentCode
    ? ` · Dept ${scenario.departmentCode}`
    : "";

  if (driverLabel) return `${base} — ${driverLabel}${dept}`;
  return `${base}${dept}`;
}

/** Suggested default title for the create flow, derived from the question. */
export function suggestScenarioTitle(
  question: string,
  departmentCode: string,
  fiscalYear: number
): string {
  const label = question.replace(/\?$/, "").trim();
  return `${label} — FY${fiscalYear} · Dept ${departmentCode}`;
}

/**
 * Returns the raw stored name when it materially differs from the friendly
 * title (a machine code or an appended suffix), so callers can surface it as a
 * secondary audit reference. Returns null when the raw name is already clean.
 */
export function rawNameReference(
  scenario: Pick<
    ScenarioSummary,
    "name" | "questionKey" | "departmentCode" | "fiscalYear"
  > & { drivers?: Array<Pick<ScenarioDriver, "type">> },
  fallbackQuestion?: string
): string | null {
  const raw = (scenario.name ?? "").trim();
  if (!raw) return null;
  const friendly = friendlyScenarioTitle(scenario, fallbackQuestion);
  return raw !== friendly ? raw : null;
}

/** Friendly driver name: replaces machine-like names with a readable label. */
export function friendlyDriverName(
  driver: Pick<ScenarioDriver, "name" | "type">
): string {
  if (!isMachineName(driver.name)) return driver.name.trim();
  return DRIVER_TYPE_LABELS[driver.type] ?? "Driver";
}
